import { useInfiniteQuery, useMutation, useQueryClient } from 'react-query';
import { getLoginActivity } from '../api/queries/account';
import { updateProfile } from '../api/mutations/account';
import { LOGIN_ACTIVITY, VALIDATE_LOGIN } from './utils/constants';

/**
 * ROAR-4942 — paged Login Activity reads for the Settings pane (AC8).
 *
 * Server-paginated via `useInfiniteQuery`, mirroring the live pattern in
 * `containers/pages/Admin/Dashboard/CustomerList.js` (a `useInView` sentinel below the table drives
 * `fetchNextPage`), with the `getNextPageParam` shape from `hooks/cicd.js`: trust an explicit
 * has-more flag when the backend sends one, otherwise infer from a short page.
 *
 * No company discriminator in the query key: Settings is gated to customer roles (plan §4
 * decision 6), so a user's company is fixed for the session and there is nothing to invalidate on.
 *
 * @param {object} [options]
 * @param {number} [options.pageSize] Rows per page.
 * @returns {import('react-query').UseInfiniteQueryResult}
 */
export const useLoginActivity = (options = {}) => {
  const { pageSize = 25 } = options;

  return useInfiniteQuery(
    [LOGIN_ACTIVITY, { pageSize }],
    ({ pageParam = 1 }) => getLoginActivity({ page: pageParam, pageSize }),
    {
      getNextPageParam: (lastPage, allPages) => {
        // An explicit flag from the backend always wins.
        if (lastPage?.has_more !== undefined && lastPage?.has_more !== null) {
          return lastPage.has_more ? (allPages?.length || 1) + 1 : undefined;
        }
        // Otherwise a short (or empty) page means we have reached the end.
        const items = lastPage?.items || [];
        if (items.length < pageSize) return undefined;
        return (allPages?.length || 1) + 1;
      },
      keepPreviousData: true,
    }
  );
};

/**
 * ROAR-4802 (AC9) — Profile write for the Settings pane.
 *
 * **Contract ratified 2026-09-28 (ROAR-4963)**: `PATCH /account/profile` writes `first_name` and
 * `last_name` only. `email` is allow-listed but immutable and is NOT sent (a present one that
 * disagrees with `users.email` 400s; an absent one is skipped), and `company_name` is rejected
 * outright — see `api/mutations/account.js` for the full allow-list and why sending an extra key
 * 400s the whole save.
 *
 * There is deliberately NO matching read hook. Every field the pane shows — name, email, role and
 * company name — already rides on the app-wide `useValidateLogin` query, which is cached with
 * `staleTime: Infinity` and mounted for the whole session; adding a second read would give the
 * modal a way to disagree with the header avatar about the user's own name.
 *
 * That is also why success invalidates `VALIDATE_LOGIN` rather than a pane-local key: renaming
 * yourself must repaint the avatar initials in `components/Header/AccountDropDown.js`, not just
 * the form you typed into.
 *
 * @param {object} [options] Extra `useMutation` options; `onSuccess` here runs before the caller's.
 * @returns {import('react-query').UseMutationResult}
 */
export const useUpdateProfile = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation(updateProfile, {
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries(VALIDATE_LOGIN);
      if (options.onSuccess) options.onSuccess(...args);
    },
  });
};
